import { useCallback, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useTreasury } from 'contracts/treasury/hooks/use-treasury'
import { GTM_EVENTS } from 'shared/constants/gtm-events'
import { useGetSmartContract } from 'shared/hooks/use-smart-contracts'
import { userVar } from 'shared/store/user'
import { handleCatchAction } from 'shared/utils/handle-catch-action'
import { showNotificationToast } from 'shared/utils/notify'
import { pushGtmEvent } from 'shared/utils/push-gtm-event'

export const useDepositSubmit = () => {
  const user = useReactiveVar(userVar)
  const isUserPlayed = user?.hasPlayed || false

  const [loading, setLoading] = useState(false)
  const { smartContractAddress, smartContractVersion } =
    useGetSmartContract('Treasury')

  const treasury = useTreasury(smartContractAddress, smartContractVersion)

  const handleDeposit = useCallback(
    async (amount: string) => {
      if (!treasury?.depositTokens) return

      setLoading(true)

      try {
        const num = Number(amount)
        if (!Number.isFinite(num) || num <= 0) {
          showNotificationToast({
            title: 'Invalid amount',
            type: 'error'
          })
          return
        }

        const tx = await treasury.depositTokens(num)

        if (!tx) {
          return
        }

        if (!isUserPlayed) {
          pushGtmEvent(GTM_EVENTS.firstDepositSuccessful, {
            deposit_value: amount,
            deposit_currency: 'USDT',
            conversion_id: tx,
            userId: user?.id || ''
          })
        } else {
          pushGtmEvent(GTM_EVENTS.depositSuccessful, {
            deposit_value: amount,
            deposit_currency: 'USDT',
            conversion_id: tx,
            userId: user?.id || ''
          })
        }

        showNotificationToast({
          title: `${amount} USDT deposited`,
          type: 'success'
        })
      } catch (err) {
        handleCatchAction(err)
      } finally {
        setLoading(false)
      }
    },
    [treasury, isUserPlayed, user?.id]
  )

  return { handleDeposit, loading }
}
